import { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Phone, MessageSquare, RefreshCw, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import api from "../lib/api";

const TwilioHealthTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/twilio/health");
      setData(r.data);
    } catch {
      setData({ configured: false, _error: true });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = () => { setRefreshing(true); load(); };

  if (loading) {
    return (
      <Card className="bg-obsidian/60 border-gold/15">
        <CardContent className="p-5 text-cream/50 text-sm">Loading Twilio health…</CardContent>
      </Card>
    );
  }

  const isOk = data && data.configured && !data._error;
  const failedOutcomes = data?.call_outcomes
    ? (data.call_outcomes["failed"] || 0) + (data.call_outcomes["unknown"] || 0)
    : 0;
  const hasIssues = data?.calls_24h > 0 && failedOutcomes / Math.max(1, data.calls_24h) > 0.3;

  const formatPhone = (e164) => {
    if (!e164) return "—";
    const m = e164.match(/^\+1?(\d{3})(\d{3})(\d{4})$/);
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
  };

  const formatTimeAgo = (iso) => {
    if (!iso) return "—";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <Card
      className={`bg-obsidian/60 border ${
        hasIssues ? "border-red-500/40" : isOk ? "border-gold/20" : "border-amber-500/40"
      }`}
      data-testid="twilio-health-tile"
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-gold" />
            <h3 className="text-cream text-sm font-medium tracking-wide">Twilio Health</h3>
            {isOk ? (
              <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] uppercase tracking-widest">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Live
              </Badge>
            ) : (
              <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] uppercase tracking-widest">
                <AlertTriangle className="w-3 h-3 mr-1" /> Not Configured
              </Badge>
            )}
          </div>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="text-cream/50 hover:text-gold transition-colors"
            data-testid="twilio-health-refresh"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>

        {!isOk ? (
          <p className="text-cream/55 text-xs leading-relaxed">
            Twilio credentials are missing from the server environment. Voice
            and SMS webhooks will not function until configured.
          </p>
        ) : (
          <>
            <div className="mb-4 pb-4 border-b border-gold/10">
              <p className="text-cream font-mono text-lg tracking-wide">
                {formatPhone(data.twilio_number)}
              </p>
              <p className="text-cream/45 text-[10px] tracking-widest uppercase mt-1">
                Forwards to {data.forward_targets.length} number{data.forward_targets.length === 1 ? "" : "s"} ·
                voicemail → {data.voicemail_email}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-black/30 border border-gold/10 rounded p-3">
                <div className="flex items-center gap-1.5 text-cream/55 text-[10px] uppercase tracking-widest mb-1.5">
                  <Phone className="w-3 h-3" /> Calls · 24h
                </div>
                <p className="text-cream text-2xl font-light">{data.calls_24h}</p>
                {data.calls_24h > 0 && (
                  <p className="text-cream/45 text-[10px] mt-1">
                    {failedOutcomes > 0 ? (
                      <span className="text-red-300">{failedOutcomes} failed/unknown</span>
                    ) : (
                      <span className="text-emerald-300">all delivered</span>
                    )}
                  </p>
                )}
              </div>
              <div className="bg-black/30 border border-gold/10 rounded p-3">
                <div className="flex items-center gap-1.5 text-cream/55 text-[10px] uppercase tracking-widest mb-1.5">
                  <MessageSquare className="w-3 h-3" /> SMS · 24h
                </div>
                <p className="text-cream text-2xl font-light">{data.sms_24h}</p>
                <p className="text-cream/45 text-[10px] mt-1">inbound</p>
              </div>
            </div>

            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2 text-cream/60">
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span className="text-cream/45 text-[10px] uppercase tracking-widest">Last Call</span>
                <span className="ml-auto text-cream/75">
                  {data.last_call ? `${formatPhone(data.last_call.from)} · ${formatTimeAgo(data.last_call.received_at)}` : "—"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-cream/60">
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span className="text-cream/45 text-[10px] uppercase tracking-widest">Last SMS</span>
                <span className="ml-auto text-cream/75">
                  {data.last_sms ? `${formatPhone(data.last_sms.from)} · ${formatTimeAgo(data.last_sms.received_at)}` : "—"}
                </span>
              </div>
            </div>

            {hasIssues && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-200 text-xs leading-relaxed">
                <strong>Elevated failure rate detected.</strong> Over 30% of recent calls did not complete. Check Twilio Monitor for details.
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TwilioHealthTile;
